// ============================================
// FRANCHISE DATA MODEL
// Core entities for franchise management
// ============================================

// ============================================
// ENUMS & CONSTANTS
// ============================================

export const OwnershipType = {
  CORPORATE: 'corporate',
  FRANCHISE: 'franchise',
  JOINT_VENTURE: 'joint_venture',
};

export const FranchiseStatus = {
  ACTIVE: 'active',
  PENDING: 'pending',
  SUSPENDED: 'suspended',
  TERMINATED: 'terminated',
};

export const ContractStatus = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  EXPIRING_SOON: 'expiring_soon',
  EXPIRED: 'expired',
  TERMINATED: 'terminated',
};

// ============================================
// FRANCHISE GROUP (IP Holder / Master Entity)
// The top-level entity that owns the brand
// ============================================

export const FranchiseGroupSchema = {
  id: 'string',                    // Unique identifier
  name: 'string',                  // Display name
  legalName: 'string',             // Registered company name
  registrationNumber: 'string',    // Company registration / VAT number
  
  // Headquarters
  headquarters: {
    address: 'string',
    city: 'string',
    postalCode: 'string',
    country: 'string',             // ISO country code
    coordinates: { lat: 'number', lng: 'number' },
  },
  
  // Contact
  contact: {
    email: 'string',
    phone: 'string',
    website: 'string',
  },
  
  // Branding
  brandId: 'string',               // Reference to brand entity
  
  // Default franchise terms (can be overridden per franchisee)
  defaultFranchiseTerms: {
    royaltyPercentage: 'number',       // % of gross sales
    marketingFeePercentage: 'number',  // % for brand marketing
    initialFranchiseFee: 'number',     // One-time fee (in base currency)
    renewalFee: 'number',              // Contract renewal fee
    contractTermYears: 'number',       // Default contract length
    territoryProtection: 'boolean',    // Exclusive territory rights
    trainingIncluded: 'boolean',
    equipmentPackageIncluded: 'boolean',
  },
  
  // System settings
  baseCurrency: 'string',          // NOK, SEK, etc.
  supportedCurrencies: ['string'],
  
  // Metadata
  foundedYear: 'number',
  founder: 'string',
  createdAt: 'datetime',
  updatedAt: 'datetime',
};

// ============================================
// FRANCHISEE (Company that operates outlets)
// Can be corporate (owned by group) or independent
// ============================================

export const FranchiseeSchema = {
  id: 'string',
  franchiseGroupId: 'string',      // Parent franchise group
  
  // Company details
  name: 'string',                  // Trading name
  legalName: 'string',             // Registered company name
  registrationNumber: 'string',    // Company registration number
  vatNumber: 'string',             // VAT registration
  
  // Type
  ownershipType: 'OwnershipType',  // corporate, franchise, joint_venture
  
  // For joint ventures
  ownershipSplit: {
    corporateShare: 'number',      // Percentage (e.g., 51)
    franchiseeShare: 'number',     // Percentage (e.g., 49)
  },
  
  // Location
  country: 'string',               // Primary country of operation
  territories: ['string'],         // Assigned territories (cities/regions)
  
  // Contact
  contact: {
    primaryPerson: 'string',
    email: 'string',
    phone: 'string',
    address: 'string',
  },
  
  // Contract details
  contract: {
    status: 'ContractStatus',
    startDate: 'date',
    endDate: 'date',
    renewalDate: 'date',
    
    // Custom terms (override group defaults)
    customTerms: {
      royaltyPercentage: 'number',
      marketingFeePercentage: 'number',
      initialFranchiseFee: 'number',
      additionalFees: [{
        name: 'string',
        amount: 'number',
        frequency: 'string',       // monthly, quarterly, annually
      }],
    },
  },
  
  // Financial
  currency: 'string',              // Operating currency
  bankDetails: {
    bankName: 'string',
    accountNumber: 'string',
    routingNumber: 'string',
    swiftCode: 'string',
  },
  
  // Settings
  settings: {
    canSetPrices: 'boolean',       // Can override menu prices
    canAddMenuItems: 'boolean',    // Can add local menu items
    canRunPromotions: 'boolean',   // Can create own promotions
    requiresApprovalFor: ['string'], // Actions requiring HQ approval
  },
  
  // Status
  status: 'FranchiseStatus',
  
  // Metadata
  createdAt: 'datetime',
  updatedAt: 'datetime',
  createdBy: 'string',
};

// ============================================
// FRANCHISE OUTLET (Individual location)
// ============================================

export const FranchiseOutletSchema = {
  id: 'string',
  code: 'string',                  // Unique outlet code (e.g., NO-OSL-01)
  
  // Relationships
  franchiseGroupId: 'string',
  franchiseeId: 'string',
  brandId: 'string',
  
  // Basic info
  name: 'string',                  // Display name
  ownershipType: 'OwnershipType',
  
  // Location
  address: {
    street: 'string',
    postalCode: 'string',
    city: 'string',
    region: 'string',
    country: 'string',
    coordinates: { lat: 'number', lng: 'number' },
  },
  
  // Regional settings
  timezone: 'string',
  currency: 'string',
  locale: 'string',
  
  // Contact
  contact: {
    phone: 'string',
    email: 'string',
    manager: 'string',
  },
  
  // Capacity
  capacity: {
    indoor: 'number',
    outdoor: 'number',
    bar: 'number',
    privateRoom: 'number',
    total: 'number',
  },
  tableCount: 'number',
  
  // Operating hours (per day)
  operatingHours: {
    monday: { open: 'time', close: 'time', closed: 'boolean' },
    // ... other days
  },
  
  // Services offered
  services: {
    dineIn: 'boolean',
    takeaway: 'boolean',
    delivery: 'boolean',
    catering: 'boolean',
    reservations: 'boolean',
    privateEvents: 'boolean',
  },
  
  // Pricing & VAT (can override franchisee/group defaults)
  pricing: {
    useFranchiseePricing: 'boolean',  // Use franchisee's price list
    priceListId: 'string',            // Custom price list if not using default
    priceModifier: 'number',          // Percentage modifier (+/- from base)
  },
  
  vat: {
    useCountryDefaults: 'boolean',
    customRates: {
      // Override specific rates if needed
    },
  },
  
  // Integrations
  integrations: {
    pos: 'string',
    delivery: ['string'],
    reservations: 'string',
    accounting: 'string',
    hr: 'string',
  },
  
  // Status
  status: 'string',                // active, temporarily_closed, permanently_closed
  openedDate: 'date',
  lastRenovation: 'date',
  
  // Metadata
  createdAt: 'datetime',
  updatedAt: 'datetime',
};

// ============================================
// FRANCHISE FEE CALCULATIONS
// ============================================

export const FeeType = {
  ROYALTY: 'royalty',
  MARKETING: 'marketing',
  TECHNOLOGY: 'technology',
  TRAINING: 'training',
  AUDIT: 'audit',
  OTHER: 'other',
};

export const FeeFrequency = {
  ONE_TIME: 'one_time',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  ANNUALLY: 'annually',
};

export const FranchiseFeeSchema = {
  id: 'string',
  franchiseeId: 'string',
  outletId: 'string',              // null = applies to all outlets
  
  feeType: 'FeeType',
  name: 'string',
  description: 'string',
  
  // Calculation method
  calculationType: 'string',       // percentage, fixed, tiered
  
  // For percentage-based fees
  percentage: 'number',
  basedOn: 'string',               // gross_sales, net_sales, profit
  
  // For fixed fees
  fixedAmount: 'number',
  currency: 'string',
  
  // For tiered fees
  tiers: [{
    minAmount: 'number',
    maxAmount: 'number',
    percentage: 'number',
  }],
  
  frequency: 'FeeFrequency',
  
  // Effective dates
  effectiveFrom: 'date',
  effectiveTo: 'date',
  
  // Status
  isActive: 'boolean',
};

// ============================================
// FRANCHISE REPORTING PERIOD
// For royalty/fee calculations
// ============================================

export const FranchiseReportingPeriodSchema = {
  id: 'string',
  franchiseeId: 'string',
  outletId: 'string',
  
  periodType: 'string',            // weekly, monthly, quarterly
  periodStart: 'date',
  periodEnd: 'date',
  
  // Sales data
  grossSales: 'number',
  netSales: 'number',
  vatCollected: 'number',
  
  // By order type
  salesByOrderType: {
    dineIn: 'number',
    takeaway: 'number',
    delivery: 'number',
  },
  
  // Calculated fees
  fees: [{
    feeType: 'FeeType',
    name: 'string',
    amount: 'number',
    calculationBasis: 'number',
    rate: 'number',
  }],
  
  totalFeesDue: 'number',
  currency: 'string',
  
  // Status
  status: 'string',                // draft, submitted, approved, paid
  submittedAt: 'datetime',
  approvedAt: 'datetime',
  approvedBy: 'string',
  paidAt: 'datetime',
  
  // Notes
  notes: 'string',
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Calculate franchise fees for a given period
 */
export function calculateFranchiseFees(salesData, feeConfig) {
  const fees = [];
  
  // Royalty fee
  if (feeConfig.royaltyPercentage > 0) {
    fees.push({
      feeType: FeeType.ROYALTY,
      name: 'Royalty Fee',
      calculationBasis: salesData.grossSales,
      rate: feeConfig.royaltyPercentage / 100,
      amount: Math.round(salesData.grossSales * (feeConfig.royaltyPercentage / 100)),
    });
  }
  
  // Marketing fee
  if (feeConfig.marketingFeePercentage > 0) {
    fees.push({
      feeType: FeeType.MARKETING,
      name: 'Marketing Fee',
      calculationBasis: salesData.grossSales,
      rate: feeConfig.marketingFeePercentage / 100,
      amount: Math.round(salesData.grossSales * (feeConfig.marketingFeePercentage / 100)),
    });
  }
  
  // Additional fees
  if (feeConfig.additionalFees) {
    feeConfig.additionalFees.forEach(fee => {
      fees.push({
        feeType: FeeType.OTHER,
        name: fee.name,
        calculationBasis: null,
        rate: null,
        amount: fee.amount,
      });
    });
  }
  
  return {
    fees,
    totalFeesDue: fees.reduce((sum, f) => sum + f.amount, 0),
  };
}

/**
 * Check if outlet belongs to franchisee
 */
export function outletBelongsToFranchisee(outlet, franchiseeId) {
  return outlet.franchiseeId === franchiseeId;
}

/**
 * Get effective fee configuration for a franchisee
 * (merges group defaults with custom terms)
 */
export function getEffectiveFeeConfig(franchiseGroup, franchisee) {
  const defaults = franchiseGroup.defaultFranchiseTerms;
  const custom = franchisee.contract?.customTerms || {};
  
  return {
    royaltyPercentage: custom.royaltyPercentage ?? defaults.royaltyPercentage,
    marketingFeePercentage: custom.marketingFeePercentage ?? defaults.marketingFeePercentage,
    initialFranchiseFee: custom.initialFranchiseFee ?? defaults.initialFranchiseFee,
    additionalFees: custom.additionalFees || [],
  };
}
